// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { AppIcon } from '@loa/ui-components';
import { formatDateTime } from '@loa/theme';
import type { NudgeHistoryDisplayProps, NudgeHistoryEntryDTO } from '../../types/nudge';
import '../PolicyList.css';

/**
 * NudgeHistoryDisplay — table of nudge emails sent for a policy. Uses
 * the shared {@code policy-list-table} class so the visual treatment
 * matches the {@link ClientPolicyTable} shown above the composer.
 * One table styling across every compose surface.
 */
export const NudgeHistoryDisplay = ({ history, loading = false }: NudgeHistoryDisplayProps) => {
  if (loading) {
    return (
      <div className="bg-white rounded-card shadow-[0_0_10px_0_rgba(0,0,0,0.10)] overflow-hidden">
        <HistoryHeader subheader="Loading history..." />
      </div>
    );
  }

  if (history.totalNudgeCount === 0) {
    return (
      <div className="bg-white rounded-card shadow-[0_0_10px_0_rgba(0,0,0,0.10)] overflow-hidden">
        <HistoryHeader subheader="No previous nudges sent for this policy" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-card shadow-[0_0_10px_0_rgba(0,0,0,0.10)] overflow-hidden">
      <HistoryHeader
        subheader={
          `${history.totalNudgeCount} nudge${history.totalNudgeCount === 1 ? '' : 's'} sent` +
          (history.lastNudgeAt ? ` — Last: ${formatDateTime(history.lastNudgeAt)}` : '')
        }
      />
      <table className="policy-list-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Sent By</th>
            <th>Subject</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {history.entries.map((entry, index) => (
            <NudgeHistoryRow key={entry.notificationId || `${entry.sentAt}-${index}`} entry={entry} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

function HistoryHeader({ subheader }: Readonly<{ subheader: string }>) {
  return (
    <div className="px-5 py-3 border-b border-lilac-04 flex items-center gap-sm">
      <AppIcon name="clock outline" size="small" />
      <div className="flex flex-col">
        <span className="text-base font-semibold text-black">Nudge History</span>
        <span className="text-xs text-grey-02">{subheader}</span>
      </div>
    </div>
  );
}

const NudgeHistoryRow = ({ entry }: { entry: NudgeHistoryEntryDTO }) => {
  const truncate = (text: string, max: number) =>
    text.length <= max ? text : text.substring(0, max) + '…';
  const isSuccess = entry.status === 'SENT';
  return (
    <tr>
      <td>
        <span className="policy-date">{formatDateTime(entry.sentAt)}</span>
      </td>
      <td>{entry.sentBy}</td>
      <td title={entry.subject}>{truncate(entry.subject, 60)}</td>
      <td>
        {isSuccess ? (
          <span className="policy-status-tag policy-status-tag--success">
            <AppIcon name="checkbox" size={16} className="policy-status-tag__icon" />
            Sent
          </span>
        ) : (
          <span
            className="policy-status-tag policy-status-tag--error"
            title={entry.errorMessage || 'Unknown error'}
          >
            <AppIcon name="rejected" size={16} className="policy-status-tag__icon" />
            Failed
          </span>
        )}
      </td>
    </tr>
  );
};

export default NudgeHistoryDisplay;
